import React from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import $ from 'jquery';
import { Carousel } from '@bingads-webui-react/primitive-unthemed';
import { ActionButton } from '@bingads-webui-react/fabric-control';
import { AdPlacementsGridPreviewPanel } from '@bingads-webui-campaign-react/ad-placements-grid-preview';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { ConditionalTooltipHost } from '@bingads-webui-campaign-react/conditional-tooltip-host';
import {
  attachAIGenAssetContextualAttribute,
} from '@bingads-webui-campaign/responsive-search-ad-strength';
import { PlayPauseButtons, isPlayPausePreviewImageEnabled } from '@bingads-webui-campaign-react/play-pause-buttons';

import { PreviewGeneral } from './preview-general';
import C from '../constants';
import { OutlookPreview } from './outlook-preview';
import getGenericImageObjectAsync from '../util/get-generic-image-object';
import getCroppedImage from '../util/get-cropped-image';
import { getPreviewCarouselStyle } from '../../style/js/mma-msan-preview-carousel-style';
import { ImageExperienceImage } from '../model/image-experience-image';

export class UnStyledMMAPreviewCarousel extends React.PureComponent {
  constructor(props) {
    super(props);

    this.isMMAV2Enabled = this.props.isMMAV2Enabled;
    this.state = {
      index: 0,
      direction: null,
      defaultImages: [],
      imageListForDisplay: [],
      isViewAllPanelOpen: false,
      aspectRatiosOrder: this.isMMAV2Enabled ? C.audienceMMAV2AspectRatiosWithoutOriginalImage : C.multimediaAdinMSANRatios,
      auto: this.props.enableAutoRotate,
    };

    this.props.activityLogger.signal('MMA: preview opened.', 'responsive ad editor');
    this.aspectRatios = this.isMMAV2Enabled ?
      _.pick(C.aspectRatiosForMMAV2, C.audienceMMAV2AspectRatiosWithoutOriginalImage) :
      _.pick(C.aspectRatios, C.multimediaAdinMSANRatios);

    if (props.isBoost200x100ImageEnabled || props.isBoostNewAspectRatioSupportEnabled) {
      this.aspectRatios.Image200x100 = C.aspectRatiosForBoost200x100.Image200x100;
      this.state.aspectRatiosOrder = C.boostMultimediaAdinMSANRatios;

      if (props.isBoostNewAspectRatioSupportEnabled) {
        _.extend(this.aspectRatios, C.aspectRatiosForBoostNewSupport);
        this.state.aspectRatiosOrder = C.boostMultimediaAdinMSANRatiosNewSupport;
      }
    }

    this.isRecommendationTypeImproveMultiMediaAdsEnabled = true;

    if (props.imageList.length === 0) {
      this.setupImagesAsync();
    }

    this.adPreviewIndexToTitle = this.setupAdPreviewIndexToTitlesMap('ImageExperience_Ad_Preview_Title_Template');
    this.adPreviewIndexToTitleInViewAll =
      this.setupAdPreviewIndexToTitlesMap('ImageExperience_Ad_Preview_Ratio_Title_Template');
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let imageListForDisplay = nextProps.imageList.length > 0
      ? nextProps.imageList : prevState.defaultImages;

    imageListForDisplay = _.filter(imageListForDisplay, image => image.Type !== 'OriginalImage');

    imageListForDisplay = _.sortBy(
      imageListForDisplay,
      image => prevState.aspectRatiosOrder.indexOf(image.Type)
    );

    return {
      imageListForDisplay,
    };
  }

  componentDidMount() {
    this.setAutoRotate();
  }

  componentDidUpdate() {
    if (_.result(_.first(this.state.imageListForDisplay), 'isRecommendedImage')) {
      // Registers AI generated asset contextual attribute popup to listen for key/mouse events
      const element = $('.iconba.iconba-Lightbulb.new-preview-carousel');
      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n: this.props.i18n,
        improveMMAOption: { type: 'Image' },
        newI18n: this.props.newI18n,
      }));
    }
  }

  componentWillUnmount() {
    this.clearAutoRotateListener();
  }

  onAddAspectRatio = (e) => {
    const index = this.getEditingImageIndex(e);
    const {
      imageListForDisplay,
    } = this.state;

    this.props.activityLogger.signal('MMA: include button clicked', 'responsive ad editor');
    this.props.onAddAspectRatio(imageListForDisplay[index].type);
  };

  onCrop = (e) => {
    const index = this.getEditingImageIndex(e);
    const {
      imageListForDisplay,
    } = this.state;

    this.props.activityLogger.signal('MMA: re-crop clicked', 'responsive ad editor');

    if (this.props.isAdCreatorEnabled) {
      this.setState({ isViewAllPanelOpen: false }, () => {
        this.props.onCropImage(imageListForDisplay[index]);
      });
    } else {
      this.props.onCropImage(imageListForDisplay[index]);
    }
  };

  onRemoveAspectRatio = (e) => {
    const index = this.getEditingImageIndex(e);
    const {
      imageListForDisplay,
    } = this.state;

    this.props.activityLogger.signal('MMA: exclude button clicked', 'responsive ad editor');

    this.props.onRemoveAspectRatio(imageListForDisplay[index].type);
  };

  setAutoRotate = () => {
    // disable auto-rotate in test
    /* istanbul ignore if */
    if (window.e2eTestEnvironment) {
      return;
    }

    if (this.state.auto === false) {
      return;
    }

    this.clearAutoRotateListener();
    const { index, imageListForDisplay } = this.state;

    const indicators = document.querySelector('.new-preview-carousel-container .carousel-indicators');

    // make sure we have the correct preview to rotate
    const previewCount = (indicators && indicators.childElementCount) || imageListForDisplay.length;
    let nextIndex = index + 1;

    /* istanbul ignore next */
    if (nextIndex >= previewCount) {
      nextIndex = 0;
    }

    this.autoRotateListener = setTimeout(() => {
      /* istanbul ignore next */
      if (this.props.enableAutoRotate) {
        this.setState({
          index: nextIndex,
          direction: 'next',
        });
      }

      /* istanbul ignore next */
      this.setAutoRotate();
    }, 3000);
  };

  getEditingImageIndex(e) {
    return _.isUndefined(e.target.dataset.index)
      ? this.state.index : parseInt(e.target.dataset.index, 10);
  }

  setupAdPreviewIndexToTitlesMap(templateKey) {
    const adPreviewIndexToTitle = [];
    const responsivePreviewsTitles =
      _.map(this.state.aspectRatiosOrder, typeKey => this.props.i18n.getString(
        templateKey,
        {
          // eslint-disable-next-line
          ratio: this.props.i18n.getString(this.aspectRatios[typeKey].stringKey),
        }
      ));

    return adPreviewIndexToTitle.concat(responsivePreviewsTitles);
  }

  setupImagesAsync() {
    const imagesToSetup = [
      this.props.genericImage,
    ];
    const setupPromises =
      _.map(imagesToSetup, imageToSetup => getGenericImageObjectAsync(imageToSetup)
        .then((imageObj) => {
          const croppedImages = getCroppedImage({
            originalImageToCrop: imageObj,
            isMultimediaAdinMSANEnabled: true,
            isMMAV2Enabled: this.isMMAV2Enabled,
            isBoost200x100ImageEnabled: this.props.isBoost200x100ImageEnabled,
            isBoostNewAspectRatioSupportEnabled: this.props.isBoostNewAspectRatioSupportEnabled,
          });

          return croppedImages;
        }));

    Promise.all(setupPromises).then(([defaultImages]) => {
      this.setState({
        defaultImages,
      });
    });
  }

  getAdPreviewTitle() {
    const { index } = this.state;

    return this.adPreviewIndexToTitle[index];
  }

  getAdPreviewHeaderInternal({ title, index, enableActionBtn }) {
    const { imageListForDisplay } = this.state;
    const selectedImage = imageListForDisplay[index];

    /* istanbul ignore if */
    if (!selectedImage) {
      return null;
    }

    const isSelectedImageIncluded = _.result(selectedImage, 'isIncluded');
    const minSize = selectedImage && this.aspectRatios[selectedImage.type].minSize;
    const hideActionBtn = !enableActionBtn
      || (!isSelectedImageIncluded && this.isInvalidImageSize(selectedImage, minSize));

    return (
      <div className="preview-header">
        <div className={classnames('ad-preview-title', 'with-additional-tag')}>
          {title}
        </div>
        <div className="flex-grow" />
        {!hideActionBtn &&
          <div className={classnames('ad-preview-actions', 'inline-title')}>
            {this.getActionButtons({
              isIncluded: isSelectedImageIncluded,
              index,
            })}
          </div>
        }
      </div>
    );
  }

  getAdPreviewHeader({ enableActionBtn }) {
    const title = this.getAdPreviewTitle();

    return this.props.isAdCreatorEnabled ?
      <React.Fragment>
        {
          this.getAdPreviewHeaderInternal({
            title,
            index: this.state.index,
            enableActionBtn,
          })
        }
      </React.Fragment> : (
        <div className="ad-preview-title">
          {title}
        </div>
      );
  }

  getActionButtons({ isIncluded, index }) {
    const { isAdCreatorEnabled } = this.props;
    const { imageListForDisplay } = this.state;
    const selectedImage = imageListForDisplay[index];

    if (isIncluded) {
      const editButtonTextKey = isAdCreatorEnabled
        ? 'ImageExperience_Edit_Aspect_Ratio' : 'ImageEditor_CropImage';

      return (
        <React.Fragment>
          <button
            className="crop-image-button btn btn-default"
            onClick={this.onCrop}
            data-index={index}
          >
            {this.props.i18n.getString(editButtonTextKey)}
          </button>
          <ConditionalTooltipHost
            condition={isAdCreatorEnabled && this.shouldDisableExcludeBtn(selectedImage)}
            content={this.props.i18n.getString('ImageExperience_Image_Placement_Required_Message')}
            calloutProps={{ gapSpace: 8 }}
          >
            <button
              disabled={this.shouldDisableExcludeBtn(selectedImage)}
              className="exclude-aspect-ratio-button btn btn-default"
              onClick={this.onRemoveAspectRatio}
              data-index={index}
            >
              {this.props.i18n.getString('ImageEditor_Exclude_AspectRatio')}
            </button>
          </ConditionalTooltipHost>
        </React.Fragment>
      );
    }

    /* istanbul ignore next */
    return (
      <button
        className="include-aspect-ratio-button btn btn-default"
        onClick={this.onAddAspectRatio}
        data-index={index}
      >
        {this.props.i18n.getString('ImageEditor_Include_AspectRatio')}
      </button>
    );
  }

  getValidationMessage(image) {
    const isSelectedImageIncluded = _.result(image, 'isIncluded');
    const minSize = image && this.aspectRatios[image.type].minSize;

    if (!isSelectedImageIncluded) {
      return this.isInvalidImageSize(image, minSize) &&
        <span className="action-label">
          {
            this.props.i18n.getString('ImageEditor_SizeValidationFailTemplate', {
              width: minSize[0],
              height: minSize[1],
            })
          }
        </span>;
    }

    return null;
  }

  getAdPreviewActions() {
    const { imageListForDisplay, index } = this.state;
    const selectedImage = imageListForDisplay[index];

    /* istanbul ignore if */
    if (!selectedImage) {
      return null;
    }

    const isSelectedImageIncluded = _.result(selectedImage, 'isIncluded');
    const hideActionButtons = this.props.isAdCreatorEnabled;
    const validationMsgElement = this.getValidationMessage(selectedImage);

    return (
      <div className="ad-preview-actions">
        {!isSelectedImageIncluded ?
          (
            <React.Fragment>
              {validationMsgElement}
              {!validationMsgElement && !hideActionButtons &&
                this.getActionButtons({ isIncluded: isSelectedImageIncluded, index })
              }
            </React.Fragment>
          ) :
          (!hideActionButtons
            && this.getActionButtons({ isIncluded: isSelectedImageIncluded, index }))
        }
      </div>
    );
  }

  setAutoState = (target) => {
    if (this.state.auto !== target) {
      if (target === false) {
        this.setState({
          auto: target,
        }, () => this.clearAutoRotateListener());
        return;
      }
      this.setState({
        auto: target,
      }, () => this.setAutoRotate());
    }
  };

  clearAutoRotateListener = () => {
    if (this.autoRotateListener) {
      clearTimeout(this.autoRotateListener);
      this.autoRotateListener = null;
    }
  };

  stop = () => {
    this.setAutoState(false);
  };

  start = () => {
    this.setAutoState(true);
  };

  isInvalidImageSize(image, minSize) {
    if (!image) {
      return false;
    }

    let widthToCheck = image.Width;
    let heightToCheck = image.Height;

    if (image.BaseImage
      && image.BaseImage.CropSettings
      && image.BaseImage.CropSettings.SourceWidth > 0
      && image.BaseImage.CropSettings.SourceHeight > 0) {
      widthToCheck = image.BaseImage.CropSettings.SourceWidth;
      heightToCheck = image.BaseImage.CropSettings.SourceHeight;
    }

    return image && minSize && (widthToCheck < minSize[0] || heightToCheck < minSize[1]);
  }

  handleSelect = (selectedIndex, e) => {
    this.setState({
      index: selectedIndex,
      direction: e.direction,
      auto: false,
    }, () => this.clearAutoRotateListener());
  };

  isPlacementActionsEnabled() {
    return this.props.totalImageListCount !== 0 && !this.props.disablePlacementActions;
  }

  shouldDisableExcludeBtn(selectedImage) {
    const { totalImageListCount, isOnlyOneIncludedImage } = this.props;
    const { imageListForDisplay } = this.state;
    const onlyOneIncluded =
      _.filter(imageListForDisplay, image => image.isIncluded, []).length === 1;

    return totalImageListCount === 1 ||
      onlyOneIncluded || isOnlyOneIncludedImage(selectedImage.type);
  }

  renderPlacementPreview(imageObj) {
    const {
      previewTexts, isAdCreatorEnabled, i18n, isBoost200x100ImageEnabled, isBoostNewAspectRatioSupportEnabled,
    } = this.props;

    const previewSettings = _.get(this.aspectRatios, [imageObj.type, 'previewSettings'], {});

    return (
      <PreviewGeneral
        image={imageObj}
        previewTexts={previewTexts}
        i18n={i18n}
        opacityChangeEnabled
        isAdCreatorEnabled={isAdCreatorEnabled}
        isMMAV2Enabled={this.isMMAV2Enabled}
        isBoost200x100ImageEnabled={isBoost200x100ImageEnabled}
        isBoostNewAspectRatioSupportEnabled={isBoostNewAspectRatioSupportEnabled}
        {...previewSettings}
      />
    );
  }

  renderBoostPreview(key) {
    const {
      previewTexts, isAdCreatorEnabled, i18n, logo, hotspots, isBoost200x100ImageEnabled, isBoostNewAspectRatioSupportEnabled,
    } = this.props;
    const { imageListForDisplay } = this.state;

    if (imageListForDisplay.length === 0) {
      return null;
    }

    const boostSettings = C.getBoostAspectRatios(i18n)[key];
    const imageObj = _.find(imageListForDisplay, image => image.Type === boostSettings.type);

    if (previewTexts.callToAction === 'Please select the language first') {
      previewTexts.callToAction = '';
    }

    if (boostSettings.previewSettings.showDismiss) {
      boostSettings.previewSettings.dismissText = i18n.getString(_TL_('Dismiss'));
    }

    return (
      <PreviewGeneral
        image={imageObj}
        previewTexts={previewTexts}
        i18n={i18n}
        logo={logo}
        hotspots={hotspots}
        opacityChangeEnabled
        customAspectRatioSettings={boostSettings}
        isAdCreatorEnabled={isAdCreatorEnabled}
        isBoost200x100ImageEnabled={isBoost200x100ImageEnabled}
        isBoostNewAspectRatioSupportEnabled={isBoostNewAspectRatioSupportEnabled}
        {...boostSettings.previewSettings}
      />
    );
  }

  render() {
    const { index, direction } = this.state;
    const {
      isAdCreatorEnabled, isMSANBoostEnabled, i18n, isMMAV2Enabled, isBoostNewAspectRatioSupportEnabled,
    } = this.props;
    const enableActionBtn = this.isPlacementActionsEnabled();
    const hideOutlookPreview = isMSANBoostEnabled;
    let enableViewAllBtn = enableActionBtn && isAdCreatorEnabled;

    const imageList = this.state.imageListForDisplay;

    if (this.isMMAV2Enabled) {
      imageList && _.each(imageList, (image) => {
        if (image && this.isInvalidImageSize(image, this.aspectRatios[image.type].minSize)) {
          image.setIncluded(false);
        }
      });
    }

    if (isMSANBoostEnabled) {
      enableViewAllBtn = false;
    }

    return (
      <div className={classnames('new-preview-carousel-container', isPlayPausePreviewImageEnabled(this.props.permissions) ? this.props.classes.PlayPauseButtonsContainer : null)}>
        {this.getAdPreviewHeader({ enableActionBtn })}
        {
          this.isRecommendationTypeImproveMultiMediaAdsEnabled
          && _.result(_.first(this.state.imageListForDisplay), 'isRecommendedImage') &&
          <div className="improve-mma-title-container">
            <span
              aria-label={this.props.i18n.getString('Ad_Strength_RSA_AI_Generated_Asset')}
              className="iconba iconba-Lightbulb new-preview-carousel"
            />
            <span>{this.props.newI18n.getString(_TL_('Recommended image'))}</span>
          </div>
        }
        {enableActionBtn && this.getAdPreviewActions()}
        <Carousel
          activeIndex={index}
          direction={direction}
          onSelect={this.handleSelect}
          controls={false}
          InjectedControls={isPlayPausePreviewImageEnabled(this.props.permissions) && this.props.enableAutoRotate ? <PlayPauseButtons
            newI18n={this.props.newI18n}
            isPlaying={this.state.auto}
            play={this.start}
            pause={this.stop}
            customizedClassName={classnames(this.props.classes.PlayPauseButtons, 'play-pause-mma-msan')}
          /> : null}
          nextLabel={this.props.i18n.getString('ImageExperience_Ad_Preview_Next')}
          prevLabel={this.props.i18n.getString('ImageExperience_Ad_Preview_Previous')}
          className={classnames({
            'new-carousel-navigation-v2': isMMAV2Enabled,
            'new-carousel-navigation': isAdCreatorEnabled,
            'boost-carousel': isMSANBoostEnabled && !isBoostNewAspectRatioSupportEnabled,
            'boost-carousel-new-support': isMSANBoostEnabled && isBoostNewAspectRatioSupportEnabled,
          })}
        >
          {
            imageList.map(imageObj => (
              <Carousel.Item key={imageObj.Type}>
                {this.renderPlacementPreview(imageObj)}
              </Carousel.Item>
            ))
          }
          {
            isMSANBoostEnabled &&
            imageList.length > 0 &&
            Object.keys(C.getBoostAspectRatios(i18n)).map(preview => (
              <Carousel.Item key={preview}>
                {this.renderBoostPreview(preview)}
              </Carousel.Item>
            ))
          }
          {!hideOutlookPreview &&
            <Carousel.Item>
              <OutlookPreview
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                isMobilePreview={false}
              />
            </Carousel.Item>
          }
          {!hideOutlookPreview &&
            <Carousel.Item>
              <OutlookPreview
                i18n={this.props.i18n}
                previewTexts={this.props.previewTexts}
                isMobilePreview
              />
            </Carousel.Item>}
        </Carousel>
        <div className="disclaimer-info">
          <div className="disclaimer-info-inline">
            {this.props.i18n.getString('ImagePreview_Disclaimer_Message_MultimediaAd')}
          </div>
        </div>
        {enableViewAllBtn &&
          <ActionButton
            className="view-placements"
            onRenderIcon={() => (<span className="iconba iconba-FullView" />)}
            onClick={() => {
              const { viewAllPlacementsActivity } = getAdCreatorInstrumentation(this.props.instrumentation, this.props.adCreatorScenario); // eslint-disable-line max-len

              viewAllPlacementsActivity.trace('UserOpenedViewAllPlacements');
              this.props.onUsedViewAllPlacements();
              this.setState({ isViewAllPanelOpen: true });
            }}
          >
            {this.props.i18n.getString('ImageExperience_View_Placements_Example')}
          </ActionButton>
        }
        {/* This grid preview is another layout of carousel preview */}
        {enableViewAllBtn &&
          <AdPlacementsGridPreviewPanel
            isOpen={this.state.isViewAllPanelOpen}
            onDismiss={() => {
              this.setState({ isViewAllPanelOpen: false });
            }}
            className={this.props.classes.gridPreviewContainer}
            cellsNumberPerRow={2}
            totalCount={_.size(imageList)}
            onRenderCell={(cellIndex) => {
              const imageAsset = imageList[cellIndex];

              return (
                <div className="placements-grid-preview-cell">
                  {this.getAdPreviewHeaderInternal({
                    title: this.adPreviewIndexToTitleInViewAll[cellIndex],
                    index: cellIndex,
                    enableActionBtn: true,
                  })}
                  {this.getValidationMessage(imageAsset)}
                  {this.renderPlacementPreview(imageAsset)}
                </div>
              );
            }}
          />
        }
      </div>
    );
  }
}

UnStyledMMAPreviewCarousel.propTypes = {
  previewTexts: PropTypes.shape({
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    callToAction: PropTypes.string,
    businessName: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  hotspots: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    desc: PropTypes.string,
    actionText: PropTypes.string,
    uri: PropTypes.string,
  })),
  genericImage: PropTypes.string.isRequired,
  imageList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceImage)),
  logo: PropTypes.instanceOf(ImageExperienceImage),
  onCropImage: PropTypes.func,
  onAddAspectRatio: PropTypes.func,
  onRemoveAspectRatio: PropTypes.func,
  isAdCreatorEnabled: PropTypes.bool,
  isMSANBoostEnabled: PropTypes.bool,
  enableAutoRotate: PropTypes.bool,
  totalImageListCount: PropTypes.number,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func,
  }).isRequired,
  isOnlyOneIncludedImage: PropTypes.func,
  classes: PropTypes.shape({
    gridPreviewContainer: PropTypes.string,
    PlayPauseButtons: PropTypes.string,
    PlayPauseButtonsContainer: PropTypes.string,
  }),
  disablePlacementActions: PropTypes.bool,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }),
  onUsedViewAllPlacements: PropTypes.func,
  permissions: PropTypes.shape({}),
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }),
  isMMAV2Enabled: PropTypes.bool,
  isBoost200x100ImageEnabled: PropTypes.bool,
  isBoostNewAspectRatioSupportEnabled: PropTypes.bool,
};

UnStyledMMAPreviewCarousel.defaultProps = {
  onCropImage: _.noop,
  onAddAspectRatio: _.noop,
  onRemoveAspectRatio: _.noop,
  isOnlyOneIncludedImage: _.noop,
  isAdCreatorEnabled: false,
  logo: null,
  isMSANBoostEnabled: false,
  enableAutoRotate: false,
  totalImageListCount: 0,
  classes: {},
  hotspots: [],
  imageList: [],
  disablePlacementActions: false,
  adCreatorScenario: null,
  instrumentation: {
    addScenario: _.noop,
  },
  onUsedViewAllPlacements: _.noop,
  permissions: {},
  newI18n: {
    getString: _.noop,
  },
  isMMAV2Enabled: false,
  isBoost200x100ImageEnabled: false,
  isBoostNewAspectRatioSupportEnabled: false,
};

export const MMAPreviewCarousel =
  withDefaultStyles(UnStyledMMAPreviewCarousel, getPreviewCarouselStyle);
